// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Übungskatalog (B5, von Thor freigegebene Mischung B+C):
// Einstieg über Körperpartie-Kacheln (C), dahinter die schnelle Liste mit
// Gruppen und Plus-Knopf (B). Die Suche steht immer oben und springt direkt
// in die Trefferliste (über alle Partien, gruppiert).
import { useMemo, useState } from "react"
import type { Exercise } from "@/lib/types"
import { bodyPartDe, equipmentDe } from "@/lib/labels"
import { pb } from "@/lib/pb"
import UebungForm from "./UebungForm"

interface Props {
  exercises: Exercise[]
  onPick: (ex: Exercise) => void
}

const LIMIT = 60

function Row({ e, withPart, onPick }: { e: Exercise; withPart: boolean; onPick: (ex: Exercise) => void }) {
  const meta = [withPart && e.body_part ? bodyPartDe(e.body_part) : "", e.equipment ? equipmentDe(e.equipment) : ""]
    .filter(Boolean).join(" · ") || "Kuratiert"
  return (
    <button type="button" className="picker-row" onClick={() => onPick(e)}>
      {e.media_slug && (
        <img
          src={`/media/exercises/${e.media_slug}.jpg`}
          alt=""
          loading="lazy"
          onError={(ev) => { ev.currentTarget.style.display = "none" }}
        />
      )}
      <span className="picker-text">
        <span className="picker-name">{e.name}</span>
        <span className="picker-meta">{meta}</span>
      </span>
      <span className="picker-add" aria-hidden="true">+</span>
    </button>
  )
}

export default function ExercisePicker({ exercises: alle, onPick }: Props) {
  const [q, setQ] = useState("")
  const [part, setPart] = useState<string | null>(null)
  const [equip, setEquip] = useState("alle")
  const [neueUebung, setNeueUebung] = useState(false)

  // Eigene Übungen der anderen Mitglieder gehören nicht in meine Auswahl
  // (der gemeinsame Katalog schon). Anzeigen bestehender Pläne bleibt davon
  // unberührt — dort werden Übungen über ihre Id geholt, nicht über diese Liste.
  const meineId = pb.authStore.record?.id ?? ""
  const exercises = useMemo(
    () => alle.filter((e) => !e.owner || e.owner === meineId),
    [alle, meineId],
  )

  // Kacheln: Körperpartien mit Anzahl, deutsch sortiert („Ohne Zuordnung" zuletzt)
  const parts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const e of exercises) counts.set(e.body_part || "", (counts.get(e.body_part || "") ?? 0) + 1)
    return [...counts.entries()].sort(([a], [b]) => {
      if (!a) return 1
      if (!b) return -1
      return bodyPartDe(a).localeCompare(bodyPartDe(b), "de")
    })
  }, [exercises])

  // Geräte-Chips: nur was es in der gewählten Partie gibt, häufigste zuerst
  const equips = useMemo(() => {
    if (part === null) return []
    const counts = new Map<string, number>()
    for (const e of exercises) {
      if ((e.body_part || "") !== part || !e.equipment) continue
      counts.set(e.equipment, (counts.get(e.equipment) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v)
  }, [exercises, part])

  const needle = q.trim().toLowerCase()
  const searching = needle.length > 0

  const results = useMemo(() => {
    if (!searching && part === null) return []
    return exercises
      .filter((e) =>
        (searching
          ? e.name.toLowerCase().includes(needle)
          : (e.body_part || "") === part && (equip === "alle" || e.equipment === equip)))
      .sort((a, b) => a.name.localeCompare(b.name, "de"))
  }, [exercises, searching, needle, part, equip])

  const shown = results.slice(0, LIMIT)

  // Suche: Treffer nach Körperpartie gruppieren (Ordnung in der langen Liste)
  const groups = useMemo(() => {
    if (!searching) return null
    const m = new Map<string, Exercise[]>()
    for (const e of shown) {
      const k = e.body_part || ""
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(e)
    }
    return [...m.entries()]
  }, [searching, shown])

  function openPart(p: string) {
    setPart(p)
    setEquip("alle")
  }

  if (neueUebung) {
    return (
      <UebungForm
        onFertig={(ex) => { setNeueUebung(false); if (ex) onPick(ex) }}
        onAbbruch={() => setNeueUebung(false)}
      />
    )
  }

  return (
    <div className="picker">
      <input
        type="text"
        placeholder="Übung suchen…"
        aria-label="Übung suchen"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {/* Fehlt eine Übung im Katalog? Selbst anlegen (Thors Wunsch Runde 12) */}
      <button type="button" className="picker-neu" onClick={() => setNeueUebung(true)}>
        + Eigene Übung anlegen
      </button>

      {/* Schritt 1 (C): Körperpartie wählen */}
      {!searching && part === null && (
        <div className="picker-parts">
          {parts.map(([p, n]) => (
            <button key={p || "ohne"} type="button" className="picker-part" onClick={() => openPart(p)}>
              <b>{p ? bodyPartDe(p) : "Ohne Zuordnung"}</b>
              <small>{n} {n === 1 ? "Übung" : "Übungen"}</small>
            </button>
          ))}
        </div>
      )}

      {/* Schritt 2 (B): schnelle Liste mit Geräte-Chips */}
      {!searching && part !== null && (
        <>
          <div className="picker-crumb">
            <button type="button" className="picker-back" onClick={() => setPart(null)}>
              ‹ Alle Partien
            </button>
            <b>{part ? bodyPartDe(part) : "Ohne Zuordnung"}</b>
          </div>
          {equips.length > 1 && (
            <div className="picker-chips">
              <button
                type="button"
                className={`chip${equip === "alle" ? " on" : ""}`}
                aria-pressed={equip === "alle"}
                onClick={() => setEquip("alle")}
              >alle Geräte</button>
              {equips.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`chip${equip === v ? " on" : ""}`}
                  aria-pressed={equip === v}
                  onClick={() => setEquip(v)}
                >{equipmentDe(v)}</button>
              ))}
            </div>
          )}
          <div className="picker-list">
            {shown.map((e) => <Row key={e.id} e={e} withPart={false} onPick={onPick} />)}
          </div>
        </>
      )}

      {/* Suche springt direkt in die Liste — über alle Partien, gruppiert */}
      {searching && (
        <div className="picker-list">
          {groups!.map(([p, list]) => (
            <div key={p || "ohne"}>
              <div className="picker-ghead">{p ? bodyPartDe(p) : "Ohne Zuordnung"}</div>
              {list.map((e) => <Row key={e.id} e={e} withPart={false} onPick={onPick} />)}
            </div>
          ))}
          {shown.length === 0 && <p className="hint">Keine Übung gefunden. Anders schreiben oder kürzer suchen.</p>}
        </div>
      )}

      {results.length > LIMIT && (
        <p className="hint picker-more">
          {shown.length} von {results.length} angezeigt — grenze mit Suche{!searching && " oder Gerät"} weiter ein.
        </p>
      )}
    </div>
  )
}
